import { Transform, Type } from 'class-transformer';
import { IsArray, IsIn, IsNumber, IsOptional, IsString, ValidateNested } from 'class-validator';

/** Aggregation windows accepted by the live equipment insights endpoint. */
export const IOT_INSIGHTS_WINDOWS = ['24h', '7d', '30d'] as const;
export type IotInsightsWindow = (typeof IOT_INSIGHTS_WINDOWS)[number];

export const IOT_COOLING_TECHNOLOGIES = ['COMPRESSOR', 'PELTIER'] as const;
type IotCoolingTechnologyValue = (typeof IOT_COOLING_TECHNOLOGIES)[number];

const IOT_DELTA_T_SOURCES = ['SENSOR', 'ESTIMATE'] as const;
type IotDeltaTSourceValue = (typeof IOT_DELTA_T_SOURCES)[number];

const IOT_THERMAL_PHASES = ['COOLING', 'WARMING', 'IDLE_UNKNOWN'] as const;
type IotThermalPhaseValue = (typeof IOT_THERMAL_PHASES)[number];

/**
 * Chart DTOs coerce numerics with `Number(value)`, which would map `null` (the
 * explicit "insufficient coverage" marker) to `0`. Keep null and undefined
 * untouched so the marker survives the transform.
 */
function nullableNumber({ value }: { value: unknown }): unknown {
  return value === null || value === undefined ? value : Number(value);
}

/** Compressor cycle statistics over the requested window. */
export class IotCycleStatsDto {
  @IsOptional()
  @IsNumber()
  @Transform(nullableNumber)
  count24h?: number | null;

  @IsOptional()
  @IsNumber()
  @Transform(nullableNumber)
  periodMedianH?: number | null;

  @IsOptional()
  @IsNumber()
  @Transform(nullableNumber)
  amplitudeMedianC?: number | null;

  @IsOptional()
  @IsNumber()
  @Transform(nullableNumber)
  dutyPct?: number | null;
}

/** Peltier ambient-vs-internal temperature difference. */
export class IotDeltaTDto {
  @IsOptional()
  @IsNumber()
  @Transform(nullableNumber)
  currentC?: number | null;

  @IsOptional()
  @IsNumber()
  @Transform(nullableNumber)
  median7dC?: number | null;

  @IsOptional()
  @IsNumber()
  @Transform(nullableNumber)
  trendPerDayC?: number | null;

  @IsOptional()
  @IsNumber()
  @Transform(nullableNumber)
  uncertaintyC?: number | null;

  /** An `ESTIMATE` is an approximation and must never be treated as a measure. */
  @IsIn(IOT_DELTA_T_SOURCES)
  source!: IotDeltaTSourceValue;
}

/** Normality band percentiles for one hour of the day. */
export class IotNormalityHourDto {
  @IsNumber()
  @Transform(({ value }) => Number(value))
  hour!: number;

  @IsNumber()
  @Transform(({ value }) => Number(value))
  p10!: number;

  @IsNumber()
  @Transform(({ value }) => Number(value))
  p50!: number;

  @IsNumber()
  @Transform(({ value }) => Number(value))
  p90!: number;
}

/** Thermal excursion above the normality band, measured as time-weighted exposure. */
export class IotThermalEventDto {
  @IsString()
  startedAt!: string;

  @IsNumber()
  @Transform(({ value }) => Number(value))
  exposureMin!: number;

  @IsNumber()
  @Transform(({ value }) => Number(value))
  amplitudeC!: number;

  @IsNumber()
  @Transform(({ value }) => Number(value))
  peakC!: number;

  /**
   * MR2: why the room got hot, when the equipment references an ambient probe.
   * 'HOT_AMBIENT' = the ambient series was at/above the absolute ambient
   * threshold during the event (≥2 probe points, event ≥2 h) — a hot ROOM, not
   * a door. Absent/null = no ambient evidence (never inferred from silence).
   */
  @IsOptional()
  @IsIn(['HOT_AMBIENT'])
  ambientTag?: 'HOT_AMBIENT' | null;

  /** Median ambient reading over the event window, when tagged (°C, 1 decimal). */
  @IsOptional()
  @IsNumber()
  ambientMedianC?: number | null;
}

/** Ingestion health of the underlying series over the window. */
export class IotDataQualityDto {
  @IsOptional()
  @IsNumber()
  @Transform(nullableNumber)
  coverageFraction?: number | null;

  @IsOptional()
  @IsNumber()
  @Transform(nullableNumber)
  metadataOnlyPct?: number | null;

  @IsOptional()
  @IsNumber()
  @Transform(nullableNumber)
  maxGapMin?: number | null;

  @IsOptional()
  @IsNumber()
  @Transform(nullableNumber)
  rssiMedian?: number | null;

  @IsOptional()
  @IsNumber()
  @Transform(nullableNumber)
  lqiMedian?: number | null;
}

/** Current position of the series inside the cycle band. */
class IotInsightsCurrentDto {
  @IsIn(IOT_THERMAL_PHASES)
  phase!: IotThermalPhaseValue;

  @IsOptional()
  @IsNumber()
  @Transform(nullableNumber)
  minutesSinceLastMinimum?: number | null;

  @IsOptional()
  @IsNumber()
  @Transform(nullableNumber)
  nextPhaseInMin?: number | null;

  @IsOptional()
  @IsNumber()
  @Transform(nullableNumber)
  positionInBand?: number | null;
}

/** Thermal-health snapshot for one piece of equipment (live endpoint). */
export class IotEquipmentInsightsDto {
  @IsIn(IOT_COOLING_TECHNOLOGIES)
  technology!: IotCoolingTechnologyValue;

  @IsIn(IOT_INSIGHTS_WINDOWS)
  window!: IotInsightsWindow;

  @ValidateNested()
  @Type(() => IotInsightsCurrentDto)
  current!: IotInsightsCurrentDto;

  /** `null` when the coverage fraction is below the spike-derived guard. */
  @IsOptional()
  @ValidateNested()
  @Type(() => IotCycleStatsDto)
  cycle?: IotCycleStatsDto | null;

  /** Compressor equipment always answers `deltaT: null`. */
  @IsOptional()
  @ValidateNested()
  @Type(() => IotDeltaTDto)
  deltaT?: IotDeltaTDto | null;

  @IsOptional()
  @IsNumber()
  @Transform(nullableNumber)
  headroomC?: number | null;

  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => IotNormalityHourDto)
  normality!: IotNormalityHourDto[];

  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => IotThermalEventDto)
  events!: IotThermalEventDto[];

  @ValidateNested()
  @Type(() => IotDataQualityDto)
  dataQuality!: IotDataQualityDto;
}

/** One day of the persisted `iot_sensor_daily_stats` history. */
export class IotDailyInsightPointDto {
  @IsString()
  date!: string;

  @IsOptional()
  @IsNumber()
  @Transform(nullableNumber)
  cyclesCount?: number | null;

  @IsOptional()
  @IsNumber()
  @Transform(nullableNumber)
  cyclePeriodMedianH?: number | null;

  @IsOptional()
  @IsNumber()
  @Transform(nullableNumber)
  cycleAmplitudeMedianC?: number | null;

  @IsOptional()
  @IsNumber()
  @Transform(nullableNumber)
  dutyPct?: number | null;

  @IsOptional()
  @IsNumber()
  @Transform(nullableNumber)
  deltaTMedianC?: number | null;

  @IsOptional()
  @IsIn(IOT_DELTA_T_SOURCES)
  deltaTSource?: IotDeltaTSourceValue | null;

  @IsOptional()
  @IsNumber()
  @Transform(nullableNumber)
  oorMinutes?: number | null;

  @IsOptional()
  @IsNumber()
  @Transform(nullableNumber)
  coverageMinutes?: number | null;

  @IsOptional()
  @IsNumber()
  @Transform(nullableNumber)
  rssiMedian?: number | null;
}
